import { useState, useEffect, useRef } from 'react';

/**
 * useMicrophone Hook
 *
 * Manages microphone access and audio stream with WebRTC audio constraints
 * Microphone is ALWAYS ON for hands-free interaction
 */
export const useMicrophone = () => {
  const [stream, setStream] = useState(null);
  const [error, setError] = useState(null);
  const [isActive, setIsActive] = useState(false);
  const audioContextRef = useRef(null);
  const analyserRef = useRef(null);

  useEffect(() => {
    let mediaStream = null;

    const initializeMicrophone = async () => {
      try {
        // Request microphone with optimal WebRTC constraints
        mediaStream = await navigator.mediaDevices.getUserMedia({
          audio: {
            echoCancellation: true,      // Prevent AI audio from being picked up
            noiseSuppression: true,       // Reduce background noise
            autoGainControl: true,        // Normalize volume
            sampleRate: 16000,            // Optimal for speech (16kHz)
            channelCount: 1,              // Mono for speech
          },
          video: false
        });

        // Create audio context for analysis
        audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)({
          sampleRate: 16000
        });

        // Create analyser node for VAD
        analyserRef.current = audioContextRef.current.createAnalyser();
        analyserRef.current.fftSize = 2048;
        analyserRef.current.smoothingTimeConstant = 0.3;

        const source = audioContextRef.current.createMediaStreamSource(mediaStream);
        source.connect(analyserRef.current);

        setStream(mediaStream);
        setIsActive(true);
        console.log('🎤 Microphone initialized successfully');
      } catch (err) {
        console.error('❌ Microphone error:', err);
        setError(err.message);
      }
    };

    initializeMicrophone();

    // Cleanup
    return () => {
      if (mediaStream) {
        mediaStream.getTracks().forEach(track => track.stop());
      }
      if (audioContextRef.current && audioContextRef.current.state !== 'closed') {
        audioContextRef.current.close();
      }
      setIsActive(false);
    };
  }, []);

  // Manual cleanup function
  const stopMicrophone = () => {
    if (stream) {
      stream.getTracks().forEach(track => {
        track.stop();
        console.log('🛑 Microphone track stopped');
      });
      setStream(null);
      setIsActive(false);
    }
    if (audioContextRef.current && audioContextRef.current.state !== 'closed') {
      audioContextRef.current.close();
      console.log('🛑 Audio context closed');
    }
  };

  return {
    stream,
    audioContext: audioContextRef.current,
    analyser: analyserRef.current,
    isActive,
    error,
    stopMicrophone
  };
};
